import { useState } from 'react';
import { createFileRoute, Navigate, useNavigate } from '@tanstack/react-router';
import { useAuth } from '@/lib/auth/authContext';
import { checkPasswordBreached } from '@/lib/auth/passwordBreach';
import { getCapturedAuthLinkType } from '@/lib/auth/inviteLinkGuard';
import { hasFreshInviteSession } from '@/lib/auth/inviteAmr';
import { decideInviteAcceptView } from '@/lib/auth/inviteAcceptView';
import { PageLoading } from '@/ui/Loader';

export const Route = createFileRoute('/invite/accept')({
  component: InviteAccept,
});

/**
 * Lands here from a `host-team-invite` invite/magic-link email. The path is
 * `/invite/accept`, not `/invite/$token` (the task brief's working name) —
 * `host-team-invite`'s `redirectTo` (`PORTAL_BUSINESS_ORIGIN + '/invite/accept'`,
 * already committed) sends GoTrue's own invite/magic-link tokens as a URL
 * hash fragment or `?code=`, not as a literal path segment, and the
 * Supabase client (`detectSessionInUrl: true`) consumes that automatically
 * on load — there is no token for a route param to capture. Ruling recorded
 * in portal/notes/T10.md (this file's own naming ruling — not
 * portal/PLAN.md, which several concurrent agents edit; see the SHARED DOC
 * FILES rule in .superpowers/…/global-constraints.md).
 *
 * The form only renders when TWO signals agree (fix round 2, finding 3 —
 * round 1's URL check alone was bypassable, see inviteAmr.ts's module doc
 * for why): `getCapturedAuthLinkType()` confirms this page load's URL
 * carried invite/recovery/magiclink/code params, AND the LIVE session's own
 * JWT `amr` claim (`hasFreshInviteSession`) carries a matching entry
 * stamped within the last 15 minutes. Either signal missing redirects to
 * `/` — an ordinary live session (crafted URL or otherwise) never reaches
 * the "set a new password" form. This is a UX gate, not the actual
 * security boundary — see portal/notes/T10.md.
 */
function InviteAccept() {
  const { supabase, session, isLoading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const linkType = getCapturedAuthLinkType();
  const isValidInviteContext = linkType !== null;
  const hasFreshAmr = !!session && hasFreshInviteSession(session.access_token, Date.now());

  const view = decideInviteAcceptView({
    isLoading,
    isValidInviteContext,
    hasSession: !!session,
    hasFreshAmr,
    done,
  });

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    setSubmitting(true);
    try {
      const breach = await checkPasswordBreached(password);
      if (breach.breached) {
        setError(
          `This password has appeared in ${breach.count.toLocaleString()} known data breaches. Choose a different one.`,
        );
        return;
      }
      if (breach.checkFailed) {
        // Soft, non-blocking notice (fix round 1, item 7) — the breach
        // check fails open (see passwordBreach.ts's ruling): a network
        // hiccup checking password safety must never itself block someone
        // from finishing joining the team. No PII in the message.
        setNotice("Couldn't verify this password against known breaches — continuing anyway.");
      }
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      setDone(true);
      setTimeout(() => navigate({ to: '/' }), 1200);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not set your password');
    } finally {
      setSubmitting(false);
    }
  }

  if (view === 'loading') {
    return <PageLoading label="Loading your invite…" />;
  }

  if (view === 'redirect-home') {
    return <Navigate to="/" />;
  }

  // BUG-5 (portal/notes/T26.md#bug-5): an anonymous visit (no session, no
  // invite context) must land on the public sign-in route, not the private
  // "/" — see decideInviteAcceptView's doc for why redirecting to "/" hung
  // the tab.
  if (view === 'redirect-signin') {
    return <Navigate to="/sign-in" />;
  }

  if (view === 'expired') {
    return (
      <section className="mx-auto max-w-sm rounded-card border border-border bg-surface p-xl shadow-card">
        <h1 className="text-h2 font-extrabold text-ink">Invite link expired</h1>
        <p className="mt-sm text-body text-ink-2">
          This invite link is no longer valid. Ask the property owner to send a new one.
        </p>
      </section>
    );
  }

  if (view === 'done') {
    return (
      <section className="mx-auto max-w-sm rounded-card border border-border bg-surface p-xl shadow-card">
        <h1 className="text-h2 font-extrabold text-ink">You&apos;re in</h1>
        <p className="mt-sm text-body text-ink-2">Taking you to your dashboard…</p>
      </section>
    );
  }

  return (
    <section className="mx-auto max-w-sm rounded-card border border-border bg-surface p-xl shadow-card">
      <h1 className="text-h2 font-extrabold text-ink">Set your password</h1>
      <p className="mt-sm text-body text-ink-2">Choose a password to finish joining the team.</p>
      <form onSubmit={handleSubmit} className="mt-lg flex flex-col gap-md">
        <label className="flex flex-col gap-xs text-body text-ink" htmlFor="invite-password">
          Password
          <input
            id="invite-password"
            name="invite-password"
            type="password"
            autoComplete="new-password"
            minLength={8}
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="rounded-input border border-border bg-surface px-md py-sm text-body text-ink"
          />
        </label>
        {notice ? <p className="text-body text-ink-2">{notice}</p> : null}
        {error ? <p className="text-body text-danger">{error}</p> : null}
        <button
          type="submit"
          disabled={submitting}
          className="rounded-button bg-canyon px-lg py-sm font-sans text-body font-bold text-white disabled:opacity-50"
        >
          {submitting ? 'Saving…' : 'Continue'}
        </button>
      </form>
    </section>
  );
}
